// Core Imports
import { api } from '@/core/lib/api'
import type { TResponse } from '@/core/types/axios'

// Module Imports
import {
  lobbyDetailSchema,
  lobbyListResponseSchema,
  paginationSchema,
} from '@/modules/lobby/lobby.schemas'
import type {
  TGameInstance,
  TLobbyDetail,
  TLobbyListQuery,
  TLobbyListResponse,
  TLobbySnapshotData,
  TParticipant,
} from '@/modules/lobby/lobby.types'
import { lobbyEndpoints } from '@/modules/lobby/lobby.constants'

export const getLobbyList = async (
  query: TLobbyListQuery
): Promise<TLobbyListResponse> => {
  const response = await api.get<TResponse<unknown>>(lobbyEndpoints.list(query))

  const parsed = lobbyListResponseSchema.parse(response.data ?? {})
  const pagination = paginationSchema.parse(
    parsed.meta?.pagination ?? {
      page: query.page,
      per_page: query.per_page,
      total: 0,
      total_page: 0,
    }
  )

  return {
    data: parsed.data,
    meta: { pagination },
  }
}

export const getLobbyDetail = async (
  lobbyId: string
): Promise<TLobbyDetail> => {
  const response = await api.get<TResponse<unknown>>(
    lobbyEndpoints.detail(lobbyId)
  )

  // Backend may wrap or return the lobby directly. Pick whichever parses.
  const candidates = [response.data?.data, response.data]
  for (const candidate of candidates) {
    const result = lobbyDetailSchema.safeParse(candidate)
    if (result.success) return result.data
  }

  console.error('[lobby.service.getLobbyDetail] invalid data', {
    lobbyId,
    lobbyData: response.data,
  })
  throw new Error(`Invalid lobby detail data for lobby ${lobbyId}`)
}

/**
 * Transform WebSocket snapshot data to TLobbyDetail format
 * Converts the comprehensive snapshot from WebSocket to the display format
 */
export const transformWebSocketSnapshotToLobbyDetail = (
  snapshot: TLobbySnapshotData
): TLobbyDetail => {
  const config = snapshot.lobby_config
  const stateData = snapshot.lobby_state_data
  const gameSnapshots = snapshot.game_snapshots || []

  const participants: TParticipant[] = Object.values(
    stateData.lobby_users || {}
  )
    .filter((user) => !stateData.kicked_user_ids?.includes(user.user_id))
    .map((user) => ({
      name: user.user_name,
      avatar: user.user_avatar,
      is_in_lobby: !stateData.inactive_users?.[user.user_id],
      ready: user.ready ?? false,
    }))

  const gameInstances: TGameInstance[] = (
    config.game_instance_configs || []
  ).map((instance, index) => {
    const gameInstanceIndex = instance.index ?? index

    let matchedSnapshot = gameSnapshots.find(
      (gameSnapshot) =>
        gameSnapshot.game_instance_config?.index === gameInstanceIndex
    )

    if (!matchedSnapshot && index < gameSnapshots.length) {
      matchedSnapshot = gameSnapshots[index]
    }

    const gameFsmDot = matchedSnapshot?.game_fsm_dot ?? null

    if (!gameFsmDot && gameSnapshots.length > 0) {
      console.warn(
        `[lobby.service.transformWebSocketSnapshot] FSM dot bulunamadı`,
        {
          searchedIndex: gameInstanceIndex,
          snapshotsCount: gameSnapshots.length,
          snapshotIndices: gameSnapshots.map(
            (s) => s.game_instance_config?.index
          ),
        }
      )
    }

    return {
      index: gameInstanceIndex,
      game_config: instance.game_config,
      question_configs:
        instance.question_configs?.map((qc) => ({
          question_count: qc.question_count ?? 10,
          question_time: qc.question_time ?? 15,
          question_difficulty: qc.question_difficulty ?? 'NORMAL',
          score_per_correct_answer: qc.score_per_correct_answer ?? 1000,
          question_categories: qc.question_categories,
        })) || [],
      game_fsm_dot: gameFsmDot,
    }
  })

  return {
    id: config.lobby_id,
    name: config.lobby_name || 'Unnamed Lobby',
    lobby_type: config.lobby_type || 'PUBLIC',
    admin_user: config.admin_userid,
    is_team_enabled: config.is_team_enabled ?? false,
    is_active: stateData.lobby_running ?? false,
    participants,
    game_instances: gameInstances,
    lobby_fsm_dot: snapshot.lobby_fsm_dot ?? null,
  }
}
